/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { useEffect, useState } from 'react';
import styles from './FHIRAccordion.module.scss';
import { copyToClipboard } from '../utils';
import { UncollapseIcon } from '../medicine/UncollapseIcon';
import { CollapseIcon } from '../medicine/CollapseIcon';
import { useAppSelector } from '../../../app/hooks';
import { selectPearlUserInfo } from '../Login/userSlice';
import { analyticsTrack } from '../analytics_utils';
import { DocumentVersionType } from '../algorithm/types';
import clsx from 'clsx';

export interface FHIRAccordionProps {
  children?: React.ReactNode;
  heading?: React.ReactNode;
  subHeading?: React.ReactNode;
  color?: string;
  onUpdate?: () => void;
  divRef?: React.RefObject<HTMLDivElement>;
  defaultCollapsed?: boolean;
  mixpanelWidgetName?: string;
  documentVersion?: DocumentVersionType; // Only for analytics
  style?: React.CSSProperties;
  hideActions?: boolean;
}

const FHIRAccordion: React.FC<FHIRAccordionProps> = ({
  children,
  heading,
  subHeading,
  color,
  onUpdate,
  divRef,
  defaultCollapsed,
  mixpanelWidgetName,
  documentVersion,
  style,
  hideActions,
}) => {
  const [expanded, setExpanded] = useState<boolean>(hideActions ? true : !defaultCollapsed);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  useEffect(() => {
    if (hideActions && !expanded) {
      setExpanded(true);
    }
  }, [expanded, hideActions]);

  useEffect(() => {
    onUpdate?.();
    // JT - Note, in testing this was no longer needed.  Left anyway.
    onUpdate && setTimeout(onUpdate, 150);
    // TODO - If I include onUpdate in deps then because of doUpdate in FhirSidebar.tsx, this sideeffect is triggering repeatedly multiple times.
    // so to avoid that removed dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleIconClick = (event: any) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
    if (mixpanelWidgetName) {
      analyticsTrack(
        'ctx_widget_action',
        {
          widget_name: mixpanelWidgetName,
          action: expanded ? 'close' : 'open',
          documentName: documentVersion?.title,
          documentId: documentVersion?.document?.id,
          documentVersionId: documentVersion?.id,
        },
        pearlUserInfo
      );
    }
  };

  const preventSummaryClick = (event: any) => {
    event.preventDefault();
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={preventSummaryClick}
      className={clsx(styles.accordion, 'fhir-border')} // Needed so FhirHighlight can find the accordion element to put the highlight border on
      slotProps={{ transition: { unmountOnExit: true, timeout: 150 } }}
      elevation={0}
      style={style}
      data-testid="accordion"
      sx={{
        p: 0,
        borderColor: color || 'transparent',
        margin: '0 !important', // Don't put margins here
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: 0,
          },
          '&.Mui-expanded': {
            minHeight: 0,
            margin: '0 !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'none !important',
            cursor: 'pointer',
            flexGrow: 1,
            maxWidth: 60,
            justifyContent: 'right',
          },
          alignItems: 'start !important',
          minHeight: '0',
          padding: '5px 5px 4px 8px',
        }}
        expandIcon={
          hideActions ? null : expanded ? (
            <div className={styles.actionsContainer} data-testid="accordion-actions">
              <Button
                id="copy-button"
                variant="copy"
                onClick={() => {
                  divRef && copyToClipboard(divRef.current);
                  if (mixpanelWidgetName) {
                    analyticsTrack(
                      'ctx_widget_action',
                      {
                        widget_name: mixpanelWidgetName,
                        action: 'copy',
                        documentName: documentVersion?.title,
                        documentId: documentVersion?.document?.id,
                        documentVersionId: documentVersion?.id,
                      },
                      pearlUserInfo
                    );
                  }
                }}
              >
                Copy
              </Button>

              <Box data-testid="collapseIconBtn" onClick={handleIconClick} className={styles.iconContainer}>
                <CollapseIcon />
              </Box>
            </div>
          ) : (
            <Box data-testid="expandIconBtn" onClick={handleIconClick} className={styles.iconContainer}>
              <UncollapseIcon />
            </Box>
          )
        }
        aria-controls="panel1-content"
        id="panel1-header"
        style={{ cursor: 'default' }}
      >
        <Box
          sx={{
            flex: 1,
          }}
          className={styles.alignCenterInRow}
        >
          <Box className={styles.accordionSummaryText}>{heading}</Box>
          {subHeading && <Box className={styles.accordionSubHeading}>{subHeading}</Box>}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        data-testid="accordionDetails"
        sx={{
          paddingY: 0,
          paddingX: '3px',
          display: 'flex',
          '.MuiAccordionDetails-root': {
            padding: '3px',
          },
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default FHIRAccordion;
